import { makeAutoObservable, runInAction } from 'mobx';
import type { RootStore } from '../../root-store';
import type {
    AdminAnalyticsOverviewDto,
    AdminImportUsageDto,
    AdminFeatureAdoptionDto,
    AdminUserActivityDto,
    AdminAssetDistributionDto,
    AdminAcquisitionDto,
    AdminChurnDto,
} from '../../../../defs/api';

export class AdminAnalyticsStore {
    private static readonly CACHE_TTL_MS = 5 * 60 * 1000;

    rootStore: RootStore;

    overview: AdminAnalyticsOverviewDto | null = null;

    importUsage: AdminImportUsageDto | null = null;

    featureAdoption: AdminFeatureAdoptionDto | null = null;

    userActivity: AdminUserActivityDto | null = null;

    assetDistribution: AdminAssetDistributionDto | null = null;

    acquisition: AdminAcquisitionDto | null = null;

    churnAnalysis: AdminChurnDto | null = null;

    loading = false;

    loadingImportUsage = false;

    loadingFeatureAdoption = false;

    loadingUserActivity = false;

    loadingAssetDistribution = false;

    loadingAcquisition = false;

    loadingChurnAnalysis = false;

    lastFetchedOverview: number | null = null;

    lastFetchedImportUsage: number | null = null;

    lastFetchedFeatureAdoption: number | null = null;

    lastFetchedUserActivity: number | null = null;

    lastFetchedAssetDistribution: number | null = null;

    lastFetchedAcquisition: number | null = null;

    lastFetchedChurnAnalysis: number | null = null;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeAutoObservable(this, {
            rootStore: false,
        });
    }

    private isStale(lastFetched: number | null): boolean {
        if (!lastFetched) {
            return true;
        }

        return Date.now() - lastFetched > AdminAnalyticsStore.CACHE_TTL_MS;
    }

    private shouldFetch<T>(data: T | null, lastFetched: number | null, force: boolean): boolean {
        if (force) {
            return true;
        }

        if (!data) {
            return true;
        }

        return this.isStale(lastFetched);
    }

    async fetchOverview(force = false): Promise<void> {
        if (this.loading || !this.shouldFetch(this.overview, this.lastFetchedOverview, force)) {
            return;
        }

        this.loading = true;
        try {
            const data =
                await this.rootStore.apiClient.adminStatisticsController.adminStatisticsControllerGetOverview();

            runInAction(() => {
                this.overview = data;
                this.lastFetchedOverview = Date.now();
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    }

    async fetchImportUsage(force = false): Promise<void> {
        if (this.loadingImportUsage || !this.shouldFetch(this.importUsage, this.lastFetchedImportUsage, force)) {
            return;
        }

        this.loadingImportUsage = true;
        try {
            const data =
                await this.rootStore.apiClient.adminStatisticsController.adminStatisticsControllerGetImportUsage();

            runInAction(() => {
                this.importUsage = data;
                this.lastFetchedImportUsage = Date.now();
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            runInAction(() => {
                this.loadingImportUsage = false;
            });
        }
    }

    async fetchFeatureAdoption(force = false): Promise<void> {
        if (
            this.loadingFeatureAdoption ||
            !this.shouldFetch(this.featureAdoption, this.lastFetchedFeatureAdoption, force)
        ) {
            return;
        }

        this.loadingFeatureAdoption = true;
        try {
            const data =
                await this.rootStore.apiClient.adminStatisticsController.adminStatisticsControllerGetFeatureAdoption();

            runInAction(() => {
                this.featureAdoption = data;
                this.lastFetchedFeatureAdoption = Date.now();
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            runInAction(() => {
                this.loadingFeatureAdoption = false;
            });
        }
    }

    async fetchUserActivity(force = false): Promise<void> {
        if (this.loadingUserActivity || !this.shouldFetch(this.userActivity, this.lastFetchedUserActivity, force)) {
            return;
        }

        this.loadingUserActivity = true;
        try {
            const data =
                await this.rootStore.apiClient.adminStatisticsController.adminStatisticsControllerGetUserActivity();

            runInAction(() => {
                this.userActivity = data;
                this.lastFetchedUserActivity = Date.now();
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            runInAction(() => {
                this.loadingUserActivity = false;
            });
        }
    }

    async fetchAssetDistribution(force = false): Promise<void> {
        if (
            this.loadingAssetDistribution ||
            !this.shouldFetch(this.assetDistribution, this.lastFetchedAssetDistribution, force)
        ) {
            return;
        }

        this.loadingAssetDistribution = true;
        try {
            const data =
                await this.rootStore.apiClient.adminStatisticsController.adminStatisticsControllerGetAssetDistribution();

            runInAction(() => {
                this.assetDistribution = data;
                this.lastFetchedAssetDistribution = Date.now();
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            runInAction(() => {
                this.loadingAssetDistribution = false;
            });
        }
    }

    async fetchAcquisition(force = false): Promise<void> {
        if (this.loadingAcquisition || !this.shouldFetch(this.acquisition, this.lastFetchedAcquisition, force)) {
            return;
        }

        this.loadingAcquisition = true;
        try {
            const data =
                await this.rootStore.apiClient.adminStatisticsController.adminStatisticsControllerGetAcquisition();

            runInAction(() => {
                this.acquisition = data;
                this.lastFetchedAcquisition = Date.now();
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            runInAction(() => {
                this.loadingAcquisition = false;
            });
        }
    }

    async fetchChurnAnalysis(force = false): Promise<void> {
        if (this.loadingChurnAnalysis || !this.shouldFetch(this.churnAnalysis, this.lastFetchedChurnAnalysis, force)) {
            return;
        }

        this.loadingChurnAnalysis = true;
        try {
            const data =
                await this.rootStore.apiClient.adminStatisticsController.adminStatisticsControllerGetChurnAnalysis();

            runInAction(() => {
                this.churnAnalysis = data;
                this.lastFetchedChurnAnalysis = Date.now();
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            runInAction(() => {
                this.loadingChurnAnalysis = false;
            });
        }
    }
}
